import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components/ui/use-toast"; // assuming you're using shadcn/ui or similar
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogClose,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DeleteUserResponse, userApi } from "@/services/userService";
import { DeleteIcon } from "lucide-react";

interface DeleteUserDialogProps {
  userId: string;
  userName: string;
}

export function DeleteUserDialog({ userId, userName }: DeleteUserDialogProps) {
  const queryClient = useQueryClient();

  const { mutate: deleteUser, isPending: deletingUser } = useMutation<
    DeleteUserResponse,
    Error,
    string
  >({
    mutationFn: (id) => userApi.deleteUser(id),
    onSuccess: () => {
      toast({
        title: "User Deleted",
        description: `User "${userName}" was successfully deleted.`,
      });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (err) => {
      toast({
        title: "Failed to Delete User",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleConfirmDelete = () => {
    deleteUser(userId);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="destructive"
          size="sm"
          className="w-full flex items-center justify-between"
        >
          Delete <DeleteIcon className="h-4 w-4" />
        </Button>
      </DialogTrigger>

      <DialogPortal>
        <DialogOverlay className="bg-black/45" />
        <DialogContent>
          <DialogTitle className="text-lg font-semibold">
            Confirm Deletion
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete <strong>{userName}</strong>? This
            action cannot be undone.
          </DialogDescription>
          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
            }}
          >
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <DialogClose asChild>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={deletingUser}
              >
                {deletingUser ? "Deleting..." : "Confirm"}
              </Button>
            </DialogClose>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
